import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { useMe } from "@/hooks/use-auth";
import {
  useUpdateWorkspace,
  useWorkspaceBonusRules,
  useWorkspaces,
} from "@/hooks/use-workspaces";

import {
  workspaceProfileSchema,
  type WorkspaceProfileFormValues,
} from "./workspace-profile-schema";

export function useCurrentWorkspace() {
  const { data: me } = useMe();
  const { data: workspacesData, isLoading: isLoadingWorkspaces } =
    useWorkspaces({ per_page: 100 });

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("");

  const resolvedWorkspaceId =
    selectedWorkspaceId || me?.activeWorkspaceId || "";

  const activeWorkspace =
    (workspacesData?.items ?? []).find((w) => w.id === resolvedWorkspaceId) ??
    null;

  const { data: bonusRules, isLoading: isLoadingBonusRules } =
    useWorkspaceBonusRules(activeWorkspace?.id ?? "");

  const { mutateAsync: updateWorkspace, isPending: isUpdating } =
    useUpdateWorkspace();

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const canUpdate =
    Boolean(me?.permissions?.includes("workspaces.update")) ||
    Boolean(me?.roleNames?.includes("Franchisor")) ||
    Boolean(me?.roleNames?.includes("Developer"));

  const workspaces = (workspacesData?.items ?? []).filter((w) => w.isActive);

  const form = useForm<WorkspaceProfileFormValues>({
    resolver: zodResolver(workspaceProfileSchema),
    defaultValues: {
      name: "",
      slug: "",
    },
  });

  const resetForm = useCallback(() => {
    if (activeWorkspace) {
      form.reset({
        name: activeWorkspace.name,
        slug: activeWorkspace.slug,
      });
    }
  }, [activeWorkspace, form]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  const openEdit = useCallback(() => {
    resetForm();
    setIsEditDialogOpen(true);
  }, [resetForm]);

  const closeEdit = useCallback(() => {
    setIsEditDialogOpen(false);
  }, []);

  async function onSubmit(values: WorkspaceProfileFormValues) {
    if (!activeWorkspace) return;

    try {
      await updateWorkspace({
        id: activeWorkspace.id,
        name: values.name,
        slug: values.slug,
      });
      toast.success("Informasi workspace berhasil diperbarui.");
      closeEdit();
    } catch {
      toast.error("Gagal memperbarui informasi workspace.");
    }
  }

  return {
    activeWorkspace,
    isLoading: isLoadingWorkspaces,
    bonusRules: bonusRules ?? [],
    isLoadingBonusRules,
    canUpdate,
    canSwitchWorkspace: Boolean(me?.canSwitchWorkspace),
    workspaces,
    selectedWorkspaceId,
    setSelectedWorkspaceId,
    form,
    isEditDialogOpen,
    setIsEditDialogOpen,
    openEdit,
    closeEdit,
    isUpdating,
    onSubmit: form.handleSubmit(onSubmit),
  };
}
